import React, { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Link } from "components/core/Link";
import { Chevron } from "components/core/Icons";

import "./HeaderMenu.scss";

const slugify = (value) => value?.toLowerCase().replace(/\s+/g, "-");

function CollapsibleSection({
	id,
	title,
	links,
	defaultExpanded,
	menuExpanded
}) {
	const [sectionExpanded, setSectionExpanded] = useState(defaultExpanded);

	useEffect(() => {
		if (!menuExpanded) {
			setSectionExpanded(defaultExpanded);
		}
	}, [menuExpanded, defaultExpanded]);

	const handleToggle = (event) => {
		setSectionExpanded(event.target.checked);
	};

	return (
		<div className="collapsible-section">
			{/* Checkbox hack (https://css-tricks.com/the-checkbox-hack/) so the
			section opens/closes natively via CSS :has(:checked) when JS is
			disabled; the onChange below just mirrors the checked state into
			React so menuExpanded can reset it. */}
			<label
				htmlFor={id}
				title={`${sectionExpanded ? "Collapse" : "Expand"} ${title}`}
				className={classnames("section-header", {
					"section-expanded": sectionExpanded,
					"section-collapsed": !sectionExpanded
				})}
			>
				{title}
				<Chevron rotate={sectionExpanded ? "up" : "down"} />
				<input
					type="checkbox"
					id={id}
					className="visually-hidden"
					checked={sectionExpanded}
					onChange={handleToggle}
				/>
			</label>
			<div className="section-links">
				{links.map(({ href, text }) => (
					<Link
						last
						color="lightBlue"
						className="link-section-item"
						href={href}
						key={text}
					>
						{text}
					</Link>
				))}
			</div>
		</div>
	);
}

CollapsibleSection.propTypes = {
	id: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	links: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string.isRequired,
			href: PropTypes.string.isRequired
		})
	).isRequired,
	defaultExpanded: PropTypes.bool,
	menuExpanded: PropTypes.bool
};

CollapsibleSection.defaultProps = {
	defaultExpanded: true
};

function HeaderMenu({
	menuName,
	links,
	sections,
	className,
	openMenuName,
	setOpenMenuName,
	isStacked
}) {
	const menuId = `header-menu-${slugify(menuName)}`;
	// Derived straight from the lifted openMenuName rather than its own
	// local state, so closing via a sibling menu's click (or the
	// click-outside handler) is reflected the moment openMenuName changes.
	const expanded = openMenuName === menuName;

	const handleExpand = (event) => {
		setOpenMenuName(event.target.checked ? menuName : null);
	};

	const renderLinks = () =>
		links?.map(({ href, text, submenu }) => (
			<Fragment key={`fragment-${text}`}>
				{submenu ? (
					<CollapsibleSection
						id={`${menuId}-${slugify(text)}`}
						title={text}
						links={submenu}
						defaultExpanded={false}
						menuExpanded={expanded}
					/>
				) : (
					<Link
						last
						className="link-menu"
						href={href}
					>
						{text}
					</Link>
				)}
			</Fragment>
		));

	const renderSections = () =>
		sections.map((section) =>
			section.links ? (
				<CollapsibleSection
					key={section.title}
					id={`${menuId}-${slugify(section.title)}`}
					title={section.title}
					links={section.links}
					defaultExpanded={section.defaultExpanded}
					menuExpanded={expanded}
				/>
			) : (
				<Link
					key={section.title}
					last
					className="link-menu"
					href={section.href}
				>
					{section.title}
				</Link>
			)
		);

	return (
		<div
			className={classnames(
				"header-menu",
				{ stacked: isStacked },
				className
			)}
		>
			{/* Checkbox hack (https://css-tricks.com/the-checkbox-hack/) so the
			menu opens/closes natively via CSS :has(:checked) when JS is
			disabled. Each menu's checkbox is independent (not a radio group)
			so Tab+Space reliably opens/closes it on its own - a shared radio
			group was tried for no-JS cross-menu auto-close, but it only let
			keyboard users switch menus via arrow keys, not Space/Enter, which
			broke the common case of just opening a menu. onChange below is a
			progressive enhancement: with JS, opening one menu closes any
			other via the lifted openMenuName; without JS, menus are
			independent. */}
			<label
				htmlFor={menuId}
				title={`${expanded ? "Close" : "Open"} ${menuName} menu`}
				className={classnames("button-menu", {
					"button-expanded": expanded,
					"button-collapsed": !expanded
				})}
			>
				{menuName}
				<Chevron rotate={expanded ? "up" : "down"} />
				<input
					type="checkbox"
					id={menuId}
					className="visually-hidden"
					checked={expanded}
					onChange={handleExpand}
				/>
			</label>
			<div className="menu-content">
				{sections ? renderSections() : renderLinks()}
			</div>
		</div>
	);
}

HeaderMenu.propTypes = {
	isStacked: PropTypes.bool,
	menuName: PropTypes.string,
	links: PropTypes.arrayOf(PropTypes.object),
	sections: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			defaultExpanded: PropTypes.bool,
			href: PropTypes.string,
			links: PropTypes.arrayOf(
				PropTypes.shape({
					text: PropTypes.string.isRequired,
					href: PropTypes.string.isRequired
				})
			)
		})
	),
	className: PropTypes.string,
	openMenuName: PropTypes.string,
	setOpenMenuName: PropTypes.func
};

export default HeaderMenu;
